/** 
 * UPGRADE.WIDGET
 * Text to explain feature to unlock
 */

import { useEffect, useState } from 'react'
import { withTranslation, WithTranslation } from 'react-i18next'
import Space from '@/components/space'
import { store } from '@/index'
import { generateTranslation } from '@/redux/translate.actions'
import Dropdown from '@/components/dropdown'
import languages from '@/translate/lang.json'
import Button from '@/components/button'
import SaveIcon from '@/components/save-icon'
import { STATUS_LOADED, STATUS_LOADING } from '@/redux/_status.types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExchangeAlt, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons'
import Chip from '@/components/chip'
import Tooltip from '@/components/tooltip'
import getEmoji from '@/utils/get-emoji.utils'
import Checkbox from '@/components/checkbox'
import { Language } from '@/redux/language.types'
 
interface OwnProps extends WithTranslation{
  sourceLang?: string             //default source language
  targetLang?: string             //default target language
  onSourceChange? : Function      //function to trigger on source language change
  onTargetChange? : Function      //function to trigger on target language change
  onTranslate: Function           //action to do after translate (set the translated text as function param)
  languages? : string[]           //array must contain at least 2 languages codes 
  sourceTexts : any[]             //texts to translate
  disableSource?: boolean         //disable dropdown selection for source language
  disableTarget?: boolean         //disable dropdown selection for target language
  allowAll?: boolean              //offer to translate into all the other languages in one go
  onClose?: Function              //in a modal : close button next to the translate button (footer of the modal)
}

const CHUNK_SIZE: number = 100

//Languages that can be translated by DeepL : the flags of lang.json (same list as the language selection)
const deeplLangAvailable: Language[] = languages.filter((x: any) => x.isTranslatable).map((lang: any) => new Language(lang))

function TranslatorWidget(props:OwnProps){
  const { t } = props

  const langAvailable: Language[] = (props.languages && props.languages.length > 0) ? deeplLangAvailable.filter((x:Language) => props.languages!.indexOf(x.code)>-1) : deeplLangAvailable

  //display dropdown for source language
  const [editSource, setEditSource] = useState<boolean>(props.disableSource ? false : true)

  //display dropdown for target language
  const [editTarget, setEditTarget] = useState<boolean>(props.disableTarget ? false : true)

  //loader while translating
  const [isLoading, setIsLoading] = useState<boolean>(false)

  //selected source language (must be the same as the text to translate)
  const [sourceLang, setSourceLang] = useState<Language>(props.sourceLang ? findLanguage(props.sourceLang) : langAvailable[0])
  
  //translate into all the other languages (template)
  const [allTargets, setAllTargets] = useState<boolean>(false)

  //selected target language
  const [targetLang, setTargetLang] = useState<Language>(props.targetLang ? findLanguage(props.targetLang) : langAvailable[0])

  useEffect(()=>{
    if(props.sourceLang){
      setSourceLang(findLanguage(props.sourceLang))
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.sourceLang])

  useEffect(()=>{
    if(props.targetLang){
      setTargetLang(findLanguage(props.targetLang))
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.targetLang])

  function changeSourceLang(lang: Language){

    const newLang: Language = new Language(lang)

    setSourceLang(newLang)
    if(props.onSourceChange){
      props.onSourceChange(newLang)
    }
  }

  function changeTargetLang(lang: Language){

    const newLang: Language = new Language(lang)

    setTargetLang(newLang)
    if(props.onTargetChange){
      props.onTargetChange(newLang)
    }
  }

  function reverseLanguages(source: Language, target: Language){
    changeSourceLang(target)
    changeTargetLang(source)
  }

  function findLanguage(langCode: string): Language{
    const language: Language = deeplLangAvailable.find((x:Language)=>x.code === langCode)!
    return language ? language : langAvailable[0]
  }

  //Name of a language with its flag in front (nothing if the language has none)
  function withFlag(lang: Language): string {
    const flag = getEmoji(lang.code)
    return (flag !== lang.code.toUpperCase() ? flag + " " : "") + lang.name
  }

  function isError(){
    return !allTargets && sourceLang.code === targetLang.code
  }

  //Translate into one language : the texts of the response are in the order of props.sourceTexts
  async function translateTo(target: Language): Promise<(string | null | undefined)[]> {
    const lang: string = target.deepLCode ?? target.code

    //Only the texts that exist in the source language and are still empty in the target one are sent to the API
    //(the others are left as they are : undefined)
    const toTranslate: number[] = props.sourceTexts
      .map((x: any, index: number) => (x[sourceLang.code] && !(x[target.code] || "").trim() ? index : -1))
      .filter((index: number) => index >= 0)
    const sourceTexts: string[] = toTranslate.map((index: number) => props.sourceTexts[index][sourceLang.code])
    let sent: (string | null)[] = []

    for(let i: number = 0; i<sourceTexts.length; i+= CHUNK_SIZE){
      const chunk: string[] = sourceTexts.slice(i, i+CHUNK_SIZE)
      const result: any = await store.dispatch(generateTranslation(chunk, sourceLang.code, lang))
      //A failed chunk gives null for each text to translate (nothing is written, the text stays missing)
      sent = [...sent, ...(Array.isArray(result) && result.length === chunk.length ? result : chunk.map(() => null))]
    }

    //Back to one entry per text of the template
    const response: (string | null | undefined)[] = props.sourceTexts.map(() => undefined)
    toTranslate.forEach((index: number, i: number) => {
      response[index] = sent[i]
    })

    return response
  }

  async function translate(){
    setIsLoading(true)

    //One language, or all the other languages one after the other
    const targets: Language[] = allTargets ? langAvailable.filter((x: Language) => x.code !== sourceLang.code) : [targetLang]

    try {
      for(let i: number = 0; i<targets.length; i++){
        const response = await translateTo(targets[i])
        await props.onTranslate(response, targets[i], i === targets.length - 1)
      }
    } finally {
      setIsLoading(false)
    }
  }
  

  return(
    <div>

      <div className="flex" onBlur={editSource ? ()=>setEditSource(false) : undefined}>
        <Space/>
        { editSource ?
        <Dropdown displayField="name"
          list={langAvailable.map((lang: Language)=>{return{...lang, id: lang.code, name: withFlag(lang)}})}
          error={isError()}
          onSelect={changeSourceLang}
          title={t("language_source")}
          active={sourceLang.code}
          value={withFlag(sourceLang)}
          isDisabled={props.disableSource}
        />
        :
        <Chip isWithMargin
          onClick={!props.disableSource ? ()=>setEditSource(true) : undefined}
          >

          { sourceLang.flag &&
          <img src={require("@/assets/default_icons/flags/" + sourceLang.flag + ".png")}
            alt={sourceLang.code}
            width="14" height="14"
            style={{ margin : '0px 6px -2px 0px' }}
          />
          }

          {sourceLang.fullName}

        </Chip>
        }
        <Space/>
      </div>

      {/** the swap button : the same space above (under the source) and under it (the title of the target has no top margin here) */}
      <div className="flex" style={{ width: "100%", justifyContent: "center", alignItems: "center", margin: "10px 0" }}>
        <Tooltip text={t("translate_swap_languages")} position="right">
          <div
            className="list-button grey-t flex items-center justify-center"
            style={{ height: 32, margin: 0 }}
            onClick={()=>reverseLanguages(sourceLang, targetLang)}>
            <FontAwesomeIcon fixedWidth icon={faExchangeAlt} rotation={90}/>
          </div>
        </Tooltip>
      </div>

      <div className="[&_.text-input-title]:!mt-0">

      { allTargets ?
      <div className="flex">
        <Space/>
        <Chip isWithMargin>{t("translate_all_languages_target")}</Chip>
        <Space/>
      </div>
      :
      <div className="flex" onBlur={editTarget ? ()=>setEditTarget(false) : undefined}>
        <Space/>
        { editTarget ?
        <Dropdown displayField="name"
          list={langAvailable.map((lang: Language)=>{return{...lang, id: lang.code, name: withFlag(lang)}})}
          error={isError()}
          onSelect={changeTargetLang}
          title={t("language_target")}
          active={targetLang.code}
          value={withFlag(targetLang)}
          isDisabled={props.disableTarget}
        />
        :
        <Chip isWithMargin
          onClick={!props.disableTarget ? ()=>setEditTarget(true) : undefined}
        >

          { targetLang.flag &&
          <img src={require("@/assets/default_icons/flags/" + targetLang.flag + ".png")}
            alt={targetLang.code}
            width="14" height="14"
            style={{ margin : '0px 6px -2px 0px' }}
          />
          }

          {targetLang.fullName}

        </Chip>



        }
        <Space/>
      </div>
      }

      { props.allowAll && langAvailable.length > 2 &&
      <div className="flex" style={{ marginTop: 12 }}>
        <Space/>
        <Checkbox active={allTargets} onClick={()=>setAllTargets(!allTargets)} text={t("translate_all_languages")}/>
        <Space/>
      </div>
      }
      </div>

      {isError() &&
      <div className="red-t">
        <FontAwesomeIcon color={"#AAA"} icon={faExclamationTriangle} />
        {" " + t("same_languages")}
      </div>
      }

      <div className="height-20"/>

      <div className="flex">
        <Space />

        <SaveIcon status={isLoading?STATUS_LOADING:STATUS_LOADED}/>

        {props.onClose &&
        <Button onClick={() => props.onClose?.()} disabled={isLoading}>
          {t("utils_close")}
        </Button>
        }

        <Button
          className={isError()?"light":"primary"}
          onClick={(isError() || isLoading) ? undefined : translate}
          isLoading={isLoading}
        >
          {t("@/translate")}
        </Button>
      </div>

    </div>
  )
}

export default withTranslation()(TranslatorWidget)
